'use client';

import { useState } from 'react';
import { formatStatsNumber } from '@/src/utils/helpers';
import { useCopyToClipboard } from '@/src/hooks/use-copy-to-clipboard';
import { Icon } from '@/src/components/ui';
import { useProfileUIStore } from '../store/profile-ui-store';
import { cn } from '@workspace/ui/lib/utils';

type StatItemProps = {
  value: number | string;
  label: string;
  isMobileView: boolean;
};

const StatItem = ({ value, label, isMobileView }: StatItemProps) => (
  <div className="py-3 pr-3">
    <p
      className={cn('font-medium text-xs text', {
        'md:text-base': !isMobileView,
      })}
    >
      {value}
    </p>
    <p
      className={cn('font-light text-[10px]', {
        'md:text-sm': !isMobileView,
      })}
    >
      {label}
    </p>
  </div>
);

type ProfileStatsProps = {
  followers: number;
  subscribers: number;
  bits: number;
};

export const ProfileStats = ({
  bits,
  followers,
  subscribers,
}: ProfileStatsProps) => {
  const stats = [
    { value: formatStatsNumber(followers) ?? 0, label: 'Followers' },
    { value: formatStatsNumber(subscribers) ?? 0, label: 'Subscribers' },
    { value: formatStatsNumber(bits) ?? 0, label: 'Stories' },
  ];
  const isMobileView = useProfileUIStore((state) => state.isMobileView);
  return (
    <div className="flex gap-3 md:px-2">
      {stats.map((stat, index) => (
        <StatItem
          isMobileView={isMobileView}
          key={index}
          value={stat.value}
          label={stat.label}
        />
      ))}
    </div>
  );
};

type ProfileLinkProps = {
  uniqueLink: string;
};

export const ProfileLink = ({ uniqueLink }: ProfileLinkProps) => {
  const { copy, copiedText } = useCopyToClipboard();
  const [isCopied, setIsCopied] = useState(false);
  const isMobileView = useProfileUIStore((state) => state.isMobileView);
  const fullLink = `player3.io/${uniqueLink}`;

  const handleCopy = async () => {
    const success = await copy(fullLink);
    if (success) {
      setIsCopied(true);
      setTimeout(() => setIsCopied(false), 2000);
    }
  };

  return (
    <div className="flex items-center">
      <p
        className={cn(
          'text-text-primary text-xs font-light hover:bg-bg-gray transition-colors ease-in-out h-[33px] rounded-[8px] flex items-center py-2 cursor-pointer',
          { 'md:text-sm md:px-2': !isMobileView }
        )}
        onClick={handleCopy}
      >
        player3.io/
        <span className="text-black font-medium p-0">{uniqueLink}</span>
      </p>
      {isCopied && (
        <span className="bg-gray-100 md:text-sm text-gray-text font-light p-2 ml-3 w-fit rounded-[8px] h-8 flex items-center gap-2 transition-all ease-in-out md:relative absolute text-xs">
          <Icon name="copy" className="size-3.5 text-gray-text" />
          Copied to clipboard
        </span>
      )}
    </div>
  );
};
